import React, { useState, useRef, useCallback, useMemo } from "react";
import DatePicker from "react-datepicker";
import { AuthService } from "../../Services/authServices";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { defaultTheme, BrandPanel, NxField, NxButton, NxIcon, FormSection } from "../../components/nx";
import ReCAPTCHA from "react-google-recaptcha";
import Select from "react-select";
import { State, City } from "country-state-city";
import {
  DocItem,
  MAX_QUALIFICATION_DOCS,
  QualificationDocsPicker,
} from "../../components/QualificationDocs/QualificationDocs";

export default function SignUp() {
  const navigate = useNavigate();
  const t = defaultTheme;

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<ReCAPTCHA>(null);
  const captchaEnabled = Boolean(process.env.REACT_APP_SITE_KEY);

  const [formData, setFormData] = useState({
    name: "", last_name: "", email: "", dob: "" as any,
    gender: "", mobile: "", password: "", cnf_password: "", city: "",
    state: "", stateCode: "", isIriaMember: "no", qualification: "", membershipNumber: "",
  });

  const [qualificationDocs, setQualificationDocs] = useState<DocItem[]>([]);

  const stateOptions = useMemo(() => State.getStatesOfCountry("IN").map(s => ({ value: s.isoCode, label: s.name })), []);
  const cityOptions = useMemo(() => {
    if (!formData.stateCode) return [];
    return City.getCitiesOfState("IN", formData.stateCode).map(c => ({ value: c.name, label: c.name }));
  }, [formData.stateCode]);

  const selectStyles = useMemo(() => ({
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 44, borderRadius: 8, fontFamily: "inherit", fontSize: 14,
      background: t.surface, borderColor: state.selectProps.hasError ? t.danger : t.border,
      boxShadow: "none",
      "&:hover": { borderColor: state.selectProps.hasError ? t.danger : t.border },
    }),
    valueContainer: (base: any) => ({ ...base, paddingLeft: 32 }),
    placeholder: (base: any) => ({ ...base, color: t.inkMute }),
    singleValue: (base: any) => ({ ...base, color: t.ink }),
    menu: (base: any) => ({ ...base, borderRadius: 8, zIndex: 10 }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: 14,
      backgroundColor: state.isSelected ? t.accent : state.isFocused ? (t.surface || "#f5f5f5") : "white",
      color: state.isSelected ? t.accentInk : t.ink,
    }),
  }), [t]);

  // Webcam state
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedPhoto, setCapturedPhoto] = useState<Blob | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const onChangeValue = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      const cleaned = value.replace(/\D/g, "");
      if (cleaned.length > 10) return;
      setFormData({ ...formData, mobile: cleaned });
      if (errors.mobile) setErrors((prev) => ({ ...prev, mobile: "" }));
      return;
    }
    setFormData({ ...formData, [name]: value });
    // Clear a field's error as soon as the user edits it
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const strongPwd = /^(?=.*[a-z])(?=.*[A-Z]).{10,}$/;

    if (!formData.name.trim()) e.name = "First name is required";
    if (!formData.last_name.trim()) e.last_name = "Last name is required";
    if (!formData.email.trim()) e.email = "Email is required";
    else if (!emailRe.test(formData.email.trim())) e.email = "Enter a valid email address";
    if (!formData.dob) e.dob = "Date of birth is required";
    else {
      const dob = new Date(formData.dob);
      const today = new Date();
      if (dob > today) e.dob = "Date of birth cannot be in the future";
      else {
        const age = today.getFullYear() - dob.getFullYear() - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
        if (age < 18) e.dob = "You must be at least 18 years old";
      }
    }
    if (!formData.gender) e.gender = "Please select a gender";
    if (!formData.mobile.trim()) e.mobile = "Mobile number is required";
    else if (!/^\d{10}$/.test(formData.mobile.trim())) e.mobile = "Mobile number must be exactly 10 digits";
    if (!formData.city.trim()) e.city = "City is required";
    if (!formData.state.trim()) e.state = "State is required";
    if (!formData.password) e.password = "Password is required";
    else if (!strongPwd.test(formData.password)) e.password = "Min. 10 chars, with upper & lowercase";
    if (!formData.cnf_password) e.cnf_password = "Please confirm your password";
    else if (formData.password !== formData.cnf_password) e.cnf_password = "Passwords do not match";

    if (formData.isIriaMember === "yes") {
      if (!formData.membershipNumber.trim()) e.membershipNumber = "Membership number is required";
      if (!formData.qualification.trim()) e.qualification = "Qualification is required";
      if (qualificationDocs.length === 0) e.qualificationDocs = "At least one Radiology PG qualification document / bonafide certificate is required";
    }

    return e;
  };

  // Leading icon for controls that are not NxField (DatePicker, react-select)
  const fieldIcon = (icon: React.ReactNode) => (
    <span style={{
      position: "absolute", top: 0, left: 13, height: 44,
      display: "flex", alignItems: "center",
      color: t.inkMute, pointerEvents: "none", zIndex: 1,
    }}>
      {icon}
    </span>
  );

  const passwordToggle = (visible: boolean, onToggle: () => void) => (
    <button
      type="button"
      onClick={onToggle}
      aria-label={visible ? "Hide password" : "Show password"}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32, borderRadius: 6,
        background: "transparent", border: "none", cursor: "pointer", color: t.inkMute,
      }}
    >
      {visible ? NxIcon.eyeOff(t.inkSoft) : NxIcon.eye(t.inkSoft)}
    </button>
  );

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setFormData({ ...formData, dob: date });
    if (errors.dob) setErrors((prev) => ({ ...prev, dob: "" }));
  };

  const openCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 480, height: 360 } });
      setStream(mediaStream);
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 50);
    } catch {
      toast.error("Unable to access camera. Please allow camera permission.");
    }
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedPhoto(blob);
        setPhotoPreview(URL.createObjectURL(blob));
      }
    }, "image/jpeg", 0.85);
    // Stop camera
    stream?.getTracks().forEach(t => t.stop());
    setStream(null);
    setCameraOpen(false);
  }, [stream]);

  const retakePhoto = useCallback(() => {
    setCapturedPhoto(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
    openCamera();
  }, [photoPreview, openCamera]);

  const signupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      toast.error("Please fix the highlighted fields");
      return;
    }
    if (!capturedPhoto) {
      toast.error("Please capture your live photo");
      return;
    }
    if (captchaEnabled && !captchaToken) {
      toast.error("Please complete the captcha verification");
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", formData.name);
      fd.append("last_name", formData.last_name);
      fd.append("email", formData.email);
      fd.append("dob", formData.dob instanceof Date ? formData.dob.toISOString() : formData.dob);
      fd.append("gender", formData.gender);
      fd.append("mobile", formData.mobile);
      fd.append("password", formData.password);
      fd.append("city", formData.city);
      fd.append("state", formData.state);
      fd.append("isIriaMember", formData.isIriaMember);

      // Always send qualification & documents
      fd.append("qualification", formData.qualification);
      qualificationDocs.forEach((doc) => {
        if (doc.file) fd.append("qualificationDoc", doc.file);
      });

      // Membership number only sent for IRIA members
      if (formData.isIriaMember === "yes") {
        fd.append("membershipNumber", formData.membershipNumber);
      }
      if (captchaToken) {
        fd.append("captchaToken", captchaToken);
      }
      fd.append("photo", capturedPhoto, "photo.jpg");

      const res = await AuthService.signup(fd);
      if (res.status === 200) {
        toast.success("Account created successfully");
        window.location.href = "/login";
      }
    } catch (e: any) {
      // reCAPTCHA tokens are single-use — reset so the user can retry
      captchaRef.current?.reset();
      setCaptchaToken(null);
      toast.error(e.response?.data || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nx-auth">
      {/* Brand panel — left */}
      <div className="nx-auth-panel nx-auth-panel-signup">
        <BrandPanel t={t} />
      </div>

      {/* Form — right */}
      <div className="nx-auth-form">
        <form onSubmit={signupSubmit} style={{ maxWidth: 520 }}>
          <h1 style={{
            // fontFamily: '"Instrument Serif", serif',
            fontSize: 34, fontWeight: 600, lineHeight: 1.15, letterSpacing: "-0.02em",
            color: t.ink, margin: "0 0 6px",
          }}>Create your account</h1>
          <div style={{ fontSize: 13.5, color: t.inkSoft }}>
            We just need a few details to set you up as a candidate.
          </div>

          <FormSection t={t} icon={NxIcon.user("#fff", 14)} title="Personal Information" />

          <div className="nx-grid-2">
            <NxField t={t} label="First name" name="name" value={formData.name} placeholder="e.g. Anika" onChange={onChangeValue} error={!!errors.name} helper={errors.name} leading={NxIcon.user(t.inkMute, 15)} required />
            <NxField t={t} label="Last name" name="last_name" value={formData.last_name} placeholder="e.g. Patel" onChange={onChangeValue} error={!!errors.last_name} helper={errors.last_name} required />
          </div>

          <div style={{ marginTop: 14 }}>
            <NxField t={t} label="Email" name="email" type="email" value={formData.email} placeholder="you@example.com" onChange={onChangeValue} error={!!errors.email} helper={errors.email} leading={NxIcon.mail(t.inkMute, 15)} required />
          </div>

          <div className="nx-grid-2" style={{ marginTop: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: t.inkMute, marginBottom: 6 }}>
                Date of birth<span style={{ color: t.danger, marginLeft: 3 }}>*</span>
              </label>
              <div style={{ position: "relative" }}>
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  placeholderText="dd / mm / yyyy"
                  dateFormat="dd / MM / yyyy"
                  maxDate={new Date(new Date().getFullYear() - 18, new Date().getMonth(), new Date().getDate())}
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  className={errors.dob ? "nx-datepicker nx-datepicker-error" : "nx-datepicker"}
                  wrapperClassName="nx-datepicker-wrapper"
                />
                {fieldIcon(NxIcon.calendar(t.inkMute, 15))}
              </div>
              {errors.dob && <div style={{ fontSize: 12, color: t.danger, marginTop: 6 }}>{errors.dob}</div>}
            </div>
            <NxField
              t={t} label="Gender" name="gender" value={formData.gender}
              options={[{ value: "male", label: "Male" }, { value: "female", label: "Female" }, { value: "other", label: "Other" }]}
              onChange={onChangeValue}
              error={!!errors.gender} helper={errors.gender}
              leading={NxIcon.user(t.inkMute, 15)}
              required
            />
          </div>

          <FormSection t={t} icon={NxIcon.phone("#fff", 14)} title="Contact Details" />

          <div>
            <NxField t={t} label="Mobile" name="mobile" value={formData.mobile} placeholder="98XXX XXX12" onChange={onChangeValue} error={!!errors.mobile} helper={errors.mobile} leading={NxIcon.phone(t.inkMute, 15)} required />
          </div>

          <div className="nx-grid-2" style={{ marginTop: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: t.inkMute, marginBottom: 6 }}>
                State<span style={{ color: t.danger, marginLeft: 3 }}>*</span>
              </label>
              <div style={{ position: "relative" }}>
                <Select
                  options={stateOptions}
                  value={formData.stateCode ? { value: formData.stateCode, label: formData.state } : null}
                  onChange={(opt: any) => {
                    setFormData({ ...formData, state: opt?.label || "", stateCode: opt?.value || "", city: "" });
                    if (errors.state) setErrors((prev) => ({ ...prev, state: "" }));
                  }}
                  placeholder="Select state..."
                  isClearable
                  styles={selectStyles}
                  {...{ hasError: !!errors.state } as any}
                />
                {fieldIcon(NxIcon.mapPin(t.inkMute, 15))}
              </div>
              {errors.state && <div style={{ fontSize: 12, color: t.danger, marginTop: 6 }}>{errors.state}</div>}
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: t.inkMute, marginBottom: 6 }}>
                City<span style={{ color: t.danger, marginLeft: 3 }}>*</span>
              </label>
              <div style={{ position: "relative" }}>
                <Select
                  options={cityOptions}
                  value={formData.city ? { value: formData.city, label: formData.city } : null}
                  onChange={(opt: any) => {
                    setFormData({ ...formData, city: opt?.value || "" });
                    if (errors.city) setErrors((prev) => ({ ...prev, city: "" }));
                  }}
                  placeholder={formData.state ? "Select city..." : "Select state first"}
                  isDisabled={!formData.state}
                  isClearable
                  styles={selectStyles}
                  {...{ hasError: !!errors.city } as any}
                />
                {fieldIcon(NxIcon.mapPin(t.inkMute, 15))}
              </div>
              {errors.city && <div style={{ fontSize: 12, color: t.danger, marginTop: 6 }}>{errors.city}</div>}
            </div>
          </div>

          <FormSection t={t} icon={NxIcon.cap("#fff", 14)} title="Professional Details" />

          <div>
            <NxField
              t={t} label="Are you an IRIA member?" name="isIriaMember" value={formData.isIriaMember}
              options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes" }]}
              onChange={onChangeValue}
              error={!!errors.isIriaMember} helper={errors.isIriaMember} required
            />
          </div>

          {/* Qualification & document — always visible, independent of IRIA membership */}
          <div style={{ marginTop: 14 }}>
            <NxField t={t} label="Qualification" name="qualification" value={formData.qualification} placeholder="e.g. MD / DNB Radiology" onChange={onChangeValue} error={!!errors.qualification} helper={errors.qualification} leading={NxIcon.cap(t.inkMute, 15)} required />
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: t.inkMute, marginBottom: 8 }}>
              Radiology PG Qualification Documents / Bonafide certificate from HOD <span style={{ color: t.danger }}>*</span>
            </label>
            <QualificationDocsPicker
              t={t}
              docs={qualificationDocs}
              max={MAX_QUALIFICATION_DOCS}
              hasError={!!errors.qualificationDocs}
              onChange={(docs) => {
                setQualificationDocs(docs);
                if (docs.length > 0 && errors.qualificationDocs) {
                  setErrors(prev => ({ ...prev, qualificationDocs: "" }));
                }
              }}
            />
            {errors.qualificationDocs && <div style={{ fontSize: 12, color: t.danger, marginTop: 4 }}>{errors.qualificationDocs}</div>}
          </div>

          {/* Membership number — only shown when IRIA member */}
          {formData.isIriaMember === "yes" && (
            <div style={{ padding: "16px", border: `1.5px solid ${t.border}`, borderRadius: 10, marginTop: 14, background: t.surface }}>
              <NxField t={t} label="Membership number" name="membershipNumber" value={formData.membershipNumber} placeholder="e.g. MEM-00123" onChange={onChangeValue} error={!!errors.membershipNumber} helper={errors.membershipNumber} required />
            </div>
          )}

          <FormSection t={t} icon={NxIcon.lock("#fff", 14)} title="Security" />

          <div className="nx-grid-2">
            <NxField
              t={t} label="Password" name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password} onChange={onChangeValue}
              placeholder="**********"
              error={!!errors.password}
              helper={errors.password || "Min. 10 characters, mixed case."}
              leading={NxIcon.lock(t.inkMute, 15)}
              trailing={passwordToggle(showPassword, () => setShowPassword((s) => !s))}
              required
            />
            <NxField
              t={t} label="Confirm password" name="cnf_password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="**********"
              value={formData.cnf_password} onChange={onChangeValue}
              error={!!errors.cnf_password}
              helper={errors.cnf_password}
              leading={NxIcon.lock(t.inkMute, 15)}
              trailing={passwordToggle(showConfirmPassword, () => setShowConfirmPassword((s) => !s))}
              required
            />
          </div>

          <FormSection t={t} icon={NxIcon.shield("#fff")} title="Identity Verification" />

          {/* Live Photo Capture */}
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: t.inkMute, marginBottom: 8 }}>
              Live photo <span style={{ color: t.danger }}>*</span>
            </label>

            {!photoPreview && !cameraOpen && (
              <button
                type="button"
                onClick={openCamera}
                style={{
                  width: "100%", padding: "14px 0", background: t.surface,
                  border: `1.5px dashed ${t.border}`, borderRadius: 10,
                  color: t.inkSoft, fontSize: 13, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                Open camera &amp; capture photo
              </button>
            )}

            {cameraOpen && (
              <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${t.border}`, background: "#000" }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: "100%", display: "block" }}
                />
                <div style={{ display: "flex", justifyContent: "center", padding: 10, background: t.surface }}>
                  <button
                    type="button"
                    onClick={capturePhoto}
                    style={{
                      padding: "8px 24px", background: t.accent, color: t.accentInk,
                      border: "none", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer",
                    }}
                  >
                    Capture
                  </button>
                </div>
              </div>
            )}

            {photoPreview && (
              <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${t.border}` }}>
                <img src={photoPreview} alt="Captured" style={{ width: "100%", display: "block" }} />
                <div style={{ display: "flex", justifyContent: "center", padding: 10, background: t.surface }}>
                  <button
                    type="button"
                    onClick={retakePhoto}
                    style={{
                      padding: "8px 24px", background: "transparent", color: t.inkSoft,
                      border: `1px solid ${t.border}`, borderRadius: 6, fontSize: 13, cursor: "pointer",
                    }}
                  >
                    Retake
                  </button>
                </div>
              </div>
            )}

            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>

          {captchaEnabled && (
            <div style={{ marginTop: 18 }}>
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={process.env.REACT_APP_SITE_KEY as string}
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 20 }}>
            <NxButton t={t} kind="primary" icon={NxIcon.arrowRight("#fff")} w="100%" type="submit" disabled={loading || (captchaEnabled && !captchaToken)}>
              {loading ? "Creating..." : "Create account"}
            </NxButton>
          </div>

          <div style={{ textAlign: "center", fontSize: 13, color: t.inkSoft, marginTop: 16 }}>
            Already a candidate?{" "}
            <a
              style={{ color: t.accent, fontWeight: 500, textDecoration: "none", cursor: "pointer" }}
              onClick={() => navigate("/login")}
            >
              Sign in
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
